import { useState, useEffect, forwardRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDemo } from '@/contexts/DemoContext';
import { DrinkButton } from '@/components/DrinkButton';
import { DrinkInfoModal } from '@/components/DrinkInfoModal';
import { DailyCelebration } from '@/components/DailyCelebration';
import { DemoConversionCta } from './DemoConversionCta';
import { calculateDaysRemaining } from '@/lib/utils';
import { Info } from 'lucide-react';

interface DemoDrinkLogSectionProps {
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

export const DemoDrinkLogSection = forwardRef<HTMLElement, DemoDrinkLogSectionProps>(
  function DemoDrinkLogSection({ onModalOpen, onModalClose }, ref) {
    const { t } = useLanguage();
    const { demoTotalDrinks, logDemoDrink, mockGroup, mockProfile } = useDemo();
    
    const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
    const [showInfoModal, setShowInfoModal] = useState(false);

    // Calculate stats
    const remainingDrinks = Math.max(0, 100 - demoTotalDrinks);

    // Auto-hide confirmation message
    useEffect(() => {
      if (confirmationMessage) {
        const timer = setTimeout(() => {
          setConfirmationMessage(null);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }, [confirmationMessage]);

    // Notify parent of modal state
    useEffect(() => {
      if (showInfoModal) {
        onModalOpen?.();
      } else {
        onModalClose?.();
      }
    }, [showInfoModal, onModalOpen, onModalClose]);

    const handleLogDrink = (drinkType: 'beer' | 'wine' | 'shot') => {
      logDemoDrink(drinkType);
      
      const messageKey = drinkType === 'beer' ? 'dashboardBeerLogged' 
        : drinkType === 'wine' ? 'dashboardWineLogged' 
        : 'dashboardShotLogged';
      setConfirmationMessage(t(messageKey));
    };

    const daysRemaining = calculateDaysRemaining(mockGroup.challenge_end);

    return (
      <section ref={ref} id="drink-log" className="min-h-[calc(100dvh-120px)] flex flex-col">
        {/* Welcome Message */}
        <div className="px-6 pt-4 text-center max-w-lg mx-auto w-full">
          <p className="text-sm text-muted-foreground">
            {t('dashboardHi')} {mockProfile.user_id}!
          </p>
          <p className="text-sm text-muted-foreground">
            {t('dashboardChallengeEnds')}{' '}
            <span className="font-bold font-mono">
              {mockGroup.invite_code}
            </span>{' '}
            {t('dashboardEndsIn')} <span className="font-bold">{daysRemaining}</span> {t('dashboardDays')}.
          </p>
        </div>

        {/* Daily Celebration (demo mode) */}
        <div className="px-6 text-center max-w-lg mx-auto w-full">
          <DailyCelebration groupId={undefined} isDemo={true} />
        </div>

        {/* Info Modal */}
        <DrinkInfoModal 
          open={showInfoModal} 
          onClose={() => setShowInfoModal(false)} 
        />

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-lg">
            {/* Stats Display */}
            <div className="text-center mb-8">
              <div className="text-6xl font-bold text-primary mb-2">
                {demoTotalDrinks}
              </div>
              <p className="text-muted-foreground">
                {t('dashboardDrinksLogged')} · {remainingDrinks} {t('dashboardRemaining')}
              </p>
            </div>



            {/* Drink Buttons */}
            <div className="grid grid-cols-3 gap-4 justify-items-center mb-6">
              <DrinkButton icon="🍺" label={t('drinkBeer')} onDoubleTap={() => handleLogDrink('beer')} disabled={false} />
              <DrinkButton icon="🍷" label={t('drinkWine')} onDoubleTap={() => handleLogDrink('wine')} disabled={false} />
              <DrinkButton icon="🥃" label={t('drinkShot')} onDoubleTap={() => handleLogDrink('shot')} disabled={false} />
            </div>

            {/* Instructions / Confirmation */}
            <div className="min-h-[60px] flex items-start justify-center">
              {confirmationMessage ? (
                <span className="px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium animate-in fade-in duration-200">
                  {confirmationMessage}
                </span>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('dashboardDoubleTap')}
                </p>
              )}
            </div>

            {/* Info Icon */}
            <div className="flex justify-center">
              <button 
                type="button" 
                onClick={() => setShowInfoModal(true)}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full"
                aria-label="Drink standards info"
              >
                <Info className="h-8 w-8" />
              </button>
            </div>

            {/* Conversion CTA */}
            <DemoConversionCta variant="inline" className="mt-4" />
          </div>
        </main>
      </section>
    );
  }
);
